import classNames from 'classnames';
import { TFunction, useTranslation } from 'gatsby-plugin-react-i18next';
import React, { ChangeEvent, useCallback, useMemo } from 'react';
import useComments from '../../hooks/useComments';
import useContactFrom, { ResponseState } from '../../hooks/useContactForm';
import Button from '../Button';
import Input from '../Input';
import Loader from '../Loader';
import Picker, { SourceOption } from '../Picker';
import QuoteAvatar from '../QuoteAvatar';
import Text, { TextTypes } from '../Text';

type ContactFormProps = {
  className?: string;
  onSuccess?: () => void;
  titleTagType?: TextTypes;
};

const getSourceOptions = (t: TFunction): SourceOption[] => [
  { value: '', body: t('contact_form_source_title'), disabled: true },
  { value: 'google', body: t('google') },
  { value: 'facebook', body: t('facebook') },
  { value: 'reference', body: t('contact_form_source_reference') },
  { value: 'guestarticle', body: t('contact_form_source_article') },
];

const ContactForm: React.FC<ContactFormProps> = ({
  className,
  onSuccess,
  titleTagType = 'h1',
}) => {
  const { t } = useTranslation();

  const comments = useComments();

  const randomComment = useMemo(
    () => comments[Math.floor(Math.random() * comments.length)],
    [comments]
  );

  const [
    values,
    errors,
    { setName, setEmail, setDescription, setSource },
    onSubmit,
    responseState,
    isProcessing,
  ] = useContactFrom({ onSuccess });

  const onSourceChange = useCallback<
    (e: ChangeEvent<HTMLSelectElement>) => void
  >((e) => setSource(e.target.value), [setSource]);

  const SubmitButton = useMemo(() => {
    return isProcessing && responseState !== ResponseState.Success ? (
      <Loader className="footer-legacy__loader" />
    ) : (
      <Button
        color="dark"
        size="medium"
        className="footer-legacy__submit-button"
        onClick={onSubmit}
      >
        {t('send')}
      </Button>
    );
  }, [isProcessing, onSubmit, responseState, t]);

  const formTitle =
    responseState === ResponseState.None
      ? t('contact_us_title')
      : responseState === ResponseState.Fail
        ? t('contact_us_error')
        : t('contact_us_success');

  const sourceOptions = useMemo(() => getSourceOptions(t), [t]);

  return (
    <div className={`contact-block-legacy ${className}`}>
      <form className="form-legacy">
        <Text
          type={titleTagType}
          color="dark"
          weight="bold"
          className="form-legacy__title h1"
        >
          {formTitle}
        </Text>
        <div
          className={classNames({
            form_success: responseState === ResponseState.Success,
          })}
        >
          <div className="personal-info-legacy form-legacy__personal-info">
            <Input
              title={t('name')}
              placeholder={t('name_placeholder_legacy')}
              value={values.name}
              error={errors.nameError}
              name="quote-name"
              className="personal-info-input-legacy"
              onValueChange={setName}
            />
            <Input
              title={t('email')}
              placeholder={t('email_placeholder_legacy')}
              value={values.email}
              error={errors.emailError}
              name="quote-email"
              className="personal-info-input-legacy"
              onValueChange={setEmail}
            />
          </div>
          <Input
            title={t('project_description_title')}
            placeholder={t('description_placeholder')}
            value={values.description}
            error={errors.descriptionError}
            name="quote-description"
            multiline
            className="personal-info-input-legacy  form-legacy__personal-info-input"
            onValueChange={setDescription}
          />

          <Picker
            options={sourceOptions}
            value={values.source}
            onChange={onSourceChange}
            className="form-legacy__source-dropdown"
          />

          <div className="footer-legacy">
            <Text type="p" color="dark" className="footer-legacy__caption">
              {t('contact_form_caption')}
            </Text>
            {SubmitButton}
          </div>
        </div>
      </form>
      <div className="quote-legacy">
        <Text
          type="p"
          weight="bold"
          color="dark"
          className="quote-legacy__title"
        >
          {t('quote_title')}
        </Text>
        <QuoteAvatar Image={randomComment.image} />
        <Text type="p" color="dark" className="quote-legacy__text">
          {randomComment.comment}
        </Text>
        <Text
          type="p"
          color="dark"
          weight="bold"
          className="quote-legacy__text"
        >
          {randomComment.author}
        </Text>
        <a href={randomComment.project.href} rel="nofollow noopener">
          <Text type="p" color="dark" className="quotee-legacy__project">
            {randomComment.project.name}
          </Text>
        </a>
      </div>
    </div>
  );
};

export default React.memo(ContactForm);
